import { useState } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Circle,
  Grid,
  GridItem,
  HStack,
  Text,
  VStack,
} from '@cdl/heartbeat-react';
import { DropdownSettings } from './dropdown/DropdownSettings';
import { SwitchForm } from './inputs/SwitchForm';
import {
  PTypes,
  QRSTypes,
  Tags,
  settings,
  settingsToDisplay,
  tagsPToDisplay,
  tagsQRSToDisplay,
  waveTypes,
} from '../../types/annotation-task-detail.type';
import {
  UseFormGetValues,
  UseFormResetField,
  UseFormSetValue,
} from 'react-hook-form';
import {
  defaultServerPTags,
  defaultServerQrsTags,
} from '../../../../tests/mocks/tags.mock';
import { ClearButton } from './ClearButton';

type Props = {
  setValue: UseFormSetValue<any>;
  resetField: UseFormResetField<any>;
  control: any;
  getValues: UseFormGetValues<any>;
};

export const SettingsForm: React.FC<Props> = ({
  setValue,
  control,
  resetField,
  getValues,
}) => {
  const initialTagsP = defaultServerPTags;
  const [selectedPTagsDisplay, setSelectedPTagsDisplay] =
    useState<Tags>(initialTagsP);
  const [selectedPTagsAnnotate, setSelectedPTagsAnnotate] =
    useState<Tags>(initialTagsP);
  const initialTagsQRS = defaultServerQrsTags;
  const [selectedQRSTagsDisplay, setSelectedQRSTagsDisplay] =
    useState<Tags>(initialTagsQRS);
  const [selectedQRSTagsAnnotate, setSelectedQRSTagsAnnotate] =
    useState<Tags>(initialTagsQRS);
  const initialSettings = Object.fromEntries(
    settings.map((tag) => [tag, true]),
  );
  const [selectedSettings, setSelectedSettings] =
    useState<Tags>(initialSettings);

  const handleSelectedTags = (values: Tags, type?: string, wave?: string) => {
    if (type === 'tags_to_display' && wave === 'P')
      setSelectedPTagsDisplay(values);
    else if (type === 'tags_to_display' && wave === 'QRS')
      setSelectedQRSTagsDisplay(values);
    else if (type === 'tags_to_annotate' && wave === 'P')
      setSelectedPTagsAnnotate(values);
    else if (type === 'tags_to_annotate' && wave === 'QRS')
      setSelectedQRSTagsAnnotate(values);
    if (type && wave)
      setValue(`settings.platane_annotation_settings.${type}.${wave}`, values, {
        shouldValidate: true,
      });
    else {
      const currentSettings = getValues('settings');

      const updatedSettings = {
        ...currentSettings,
        ...values,
      };

      setSelectedSettings(updatedSettings);
      setValue('settings', updatedSettings, {
        shouldValidate: true,
      });
    }
  };

  const [isClearDisplay, setIsClearDisplay] = useState(false);
  const [isClearAnnotate, setIsClearAnnotate] = useState(false);

  const clearAllDisplayData = () => {
    setSelectedPTagsDisplay(
      Object.fromEntries(PTypes.map((tag) => [tag, false])),
    );
    setSelectedQRSTagsDisplay(
      Object.fromEntries(QRSTypes.map((tag) => [tag, false])),
    );
    resetField('settings.platane_annotation_settings.waves_to_display', {
      defaultValue: Object.fromEntries(waveTypes.map((tag) => [tag, false])),
    });
    setValue(
      'settings.platane_annotation_settings.tags_to_display.QRS',
      Object.fromEntries(QRSTypes.map((tag) => [tag, false])),
    );
    setValue(
      'settings.platane_annotation_settings.tags_to_display.P',
      Object.fromEntries(PTypes.map((tag) => [tag, false])),
    );
    setIsClearDisplay(true);
  };

  const clearAllAnnotateData = () => {
    setSelectedPTagsAnnotate(
      Object.fromEntries(PTypes.map((tag) => [tag, false])),
    );
    setSelectedQRSTagsAnnotate(
      Object.fromEntries(QRSTypes.map((tag) => [tag, false])),
    );
    resetField('settings.platane_annotation_settings.waves_to_annotate', {
      defaultValue: Object.fromEntries(waveTypes.map((tag) => [tag, false])),
    });
    setValue(
      'settings.platane_annotation_settings.tags_to_annotate.QRS',
      Object.fromEntries(QRSTypes.map((tag) => [tag, false])),
    );
    setValue(
      'settings.platane_annotation_settings.tags_to_annotate.P',
      Object.fromEntries(PTypes.map((tag) => [tag, false])),
    );
    setIsClearAnnotate(true);
  };

  return (
    <>
      <Accordion defaultIndex={[0]} allowMultiple>
        <AccordionItem bgColor='white'>
          <AccordionButton
            justifyContent='space-between'
            p='l'
            borderBottom='1px'
            borderColor='neutral.100'
          >
            <HStack gap={2}>
              <Circle size='16px' m='s' border='1px' />
              <Text textStyle='page-subtitle'>Settings</Text>
            </HStack>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel p='xl'>
            <DropdownSettings
              data={settingsToDisplay}
              preSelected={settings}
              selectedData={selectedSettings}
              handleDataChange={handleSelectedTags}
              setIsClear={setIsClearDisplay}
              label={'General Settings'}
            />
            <Grid templateColumns='repeat(4, 1fr)' gap='xxl' pt='xl'>
              <GridItem colSpan={2}>
                <Text textStyle='label' color='neutral.300' mb='l'>
                  Settings to display
                </Text>
                <VStack
                  spacing='m'
                  alignItems='stretch'
                  p='m'
                  border='1px'
                  borderColor='neutral.200'
                  borderRadius='4px'
                  my='m'
                >
                  <Text textStyle='subtitle'>Waves to display</Text>
                  <SwitchForm control={control} type='waves_to_display' />
                </VStack>
                <DropdownSettings
                  data={tagsPToDisplay}
                  preSelected={PTypes}
                  selectedData={selectedPTagsDisplay}
                  handleDataChange={handleSelectedTags}
                  type='tags_to_display'
                  wave='P'
                  clear={isClearDisplay}
                  setIsClear={setIsClearDisplay}
                  label={'Tags to display'}
                />
                <DropdownSettings
                  data={tagsQRSToDisplay}
                  preSelected={QRSTypes}
                  selectedData={selectedQRSTagsDisplay}
                  handleDataChange={handleSelectedTags}
                  type='tags_to_display'
                  wave='QRS'
                  clear={isClearDisplay}
                  setIsClear={setIsClearDisplay}
                  label={'Tags to display'}
                />
                <Box mt='xl'>
                  <ClearButton
                    clearAllData={clearAllDisplayData}
                    label={'Clear settings to display'}
                  />
                </Box>
              </GridItem>
              <GridItem colSpan={2}>
                <Text textStyle='label' color='neutral.300' mb='l'>
                  Settings to annotate
                </Text>
                <VStack
                  spacing='m'
                  alignItems='stretch'
                  p='m'
                  border='1px'
                  borderColor='neutral.200'
                  borderRadius='4px'
                  my='m'
                >
                  <Text textStyle='subtitle'>Waves to annotate</Text>
                  <SwitchForm control={control} type='waves_to_annotate' />
                </VStack>
                <DropdownSettings
                  data={tagsPToDisplay}
                  preSelected={PTypes}
                  selectedData={selectedPTagsAnnotate}
                  handleDataChange={handleSelectedTags}
                  type='tags_to_annotate'
                  wave='P'
                  clear={isClearAnnotate}
                  setIsClear={setIsClearAnnotate}
                  label={'Tags to annotate'}
                />
                <DropdownSettings
                  data={tagsQRSToDisplay}
                  preSelected={QRSTypes}
                  selectedData={selectedQRSTagsAnnotate}
                  handleDataChange={handleSelectedTags}
                  type='tags_to_annotate'
                  wave='QRS'
                  clear={isClearAnnotate}
                  setIsClear={setIsClearAnnotate}
                  label={'Tags to annotate'}
                />
                <Box mt='xl'>
                  <ClearButton
                    clearAllData={clearAllAnnotateData}
                    label={'Clear settings to annotate'}
                  />
                </Box>
              </GridItem>
            </Grid>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </>
  );
};
